'use client';

import { useMemo, useState } from 'react';
import { parseHex, buildPalettes } from '@/lib/browser-color-tools-extra';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function readableTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#000000';
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 140 ? '#111111' : '#FFFFFF';
}

export default function BrowserColorPaletteGenerator() {
  const [base, setBase] = useState('#2563EB');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const rgb = useMemo(() => parseHex(base), [base]);
  const palettes = useMemo(() => (rgb ? buildPalettes(rgb) : null), [rgb]);

  async function handleCopy(label: string, value: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Color</span>
        <h2 className="tool-live-title">Color palette generator</h2>
        <p className="tool-live-subtitle">
          Turn one base color into complementary, analogous, triadic, monochromatic, and shade/tint
          palettes.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Base color</span>
              <input
                aria-label="Base color picker"
                onChange={(e) => setBase(e.target.value.toUpperCase())}
                style={{
                  width: '100%',
                  height: '3rem',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                }}
                type="color"
                value={base.match(/^#[0-9a-fA-F]{6}$/) ? base : '#2563EB'}
              />
            </label>
            <label className="tool-live-field">
              <span>HEX value</span>
              <input
                onChange={(e) => {
                  const next = e.target.value.trim();
                  setBase(next.startsWith('#') ? next.toUpperCase() : `#${next.toUpperCase()}`);
                }}
                placeholder="#2563EB"
                type="text"
                value={base}
              />
            </label>
          </div>
          {!rgb ? (
            <p className="status-message" style={{ color: '#dc3545' }}>
              Enter a valid 6-digit hex color (for example #2563EB).
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {palettes ? (
            <div style={{ display: 'grid', gap: '1.25rem' }}>
              {palettes.map((group) => (
                <div key={group.kind}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.4rem',
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{group.label}</span>
                    <button
                      className="button button-secondary"
                      onClick={() => handleCopy(group.label, group.colors.join(', '))}
                      type="button"
                    >
                      Copy row
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${group.colors.length}, 1fr)`,
                      gap: '0.35rem',
                    }}
                  >
                    {group.colors.map((hex, index) => (
                      <button
                        key={`${group.kind}-${index}-${hex}`}
                        onClick={() => handleCopy(hex, hex)}
                        style={{
                          background: hex,
                          color: readableTextColor(hex),
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: '8px',
                          height: '64px',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          padding: '0.3rem',
                          cursor: 'pointer',
                        }}
                        title={`Copy ${hex}`}
                        type="button"
                      >
                        {hex}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <p className="tool-live-panel-note">
            Tap any swatch to copy its hex. Generated locally in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
